import React, { useCallback, useMemo, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import type { StripeElementsOptions } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { createContext } from '@shared/utils/createContext';
import type { StripeElementsContextValue, StripeElementsProviderLazyConfig } from './StripeElements.types';

const SETUP_DEFAULTS: Pick<StripeElementsOptions, 'mode' | 'appearance'> = {
  mode: 'setup',
  appearance: { variables: { borderRadius: '25px' } },
};

const [StripeElementsContextProvider, useStripeElementsContext] = createContext<StripeElementsContextValue>({
  name: 'StripeElements',
});
export { useStripeElementsContext };

/**
 * Wraps Stripe's `<Elements>` with lazy, first-one-wins config injection.
 *
 * Rendered high in the tree before the correct Stripe public key is known.
 * A descendant calls `setLazyConfig()` once the key is resolved from GraphQL;
 * subsequent calls are ignored (first writer wins).
 */
export const StripeElements: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const [lazyConfig, setLazyConfigState] = useState<StripeElementsProviderLazyConfig | null>(null);

  const setLazyConfig = useCallback((config: StripeElementsProviderLazyConfig) => {
    setLazyConfigState(prev => (prev ? prev : config));
  }, []);

  const stripePromise = useMemo(() => (lazyConfig ? loadStripe(lazyConfig.publicKey) : null), [lazyConfig]);

  const options: StripeElementsOptions | undefined = useMemo(
    () =>
      lazyConfig
        ? {
            ...SETUP_DEFAULTS,
            // Stripe uses ISO 4217 currency codes + lowercase
            currency: lazyConfig.currencyCode.toLowerCase(),
            // allows us to define Payment Method constraints for Express Checkout
            payment_method_configuration: lazyConfig.expressPaymentMethodsConfigurationId,
          }
        : undefined,
    [lazyConfig],
  );

  return (
    <StripeElementsContextProvider value={{ setLazyConfig }}>
      <Elements
        stripe={stripePromise}
        options={options}
      >
        {children}
      </Elements>
    </StripeElementsContextProvider>
  );
};
